import { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshCw, Users } from "lucide-react";
import { AppLayout } from "../../../components/layout";
import { ContentCard, DataTable, EmptyState, LoadingState, ui } from "../../../components/ui";
import { TablePagination } from "../../leave-management/components/TablePagination";
import { cn } from "../../../lib/utils";
import {
  CLAIM_CATEGORY,
  CLAIM_CATEGORY_LABEL_KEY,
  CLAIM_STATUS_LABEL_KEY,
  REPORT_CLAIM_STATUSES,
  isReportableClaim,
  abbreviationFor,
  claimSubmitterName,
  formatAmount,
  formatClaimPeriod,
  formatMonthKey,
  formatStamp,
} from "../constants/claimConstants";
import {
  employeeOptionsOf,
  filterClaims,
  monthKeysOf,
} from "../lib/claimListFilters";
import {
  ALL_CLAIMS_PATH,
  CANONICAL_ROLE,
  DETAIL_ORIGIN,
  claimDetailPath,
} from "../constants/approvalConstants";
import { fetchTeamClaims } from "../../../services/conveyanceClaimService";
import { ClaimStatusBadge } from "../components/ClaimStatusBadge";
import { ClaimFilterBar } from "../components/ClaimFilterBar";
import { useClaimCurrencies } from "../components/PolicyGate";
import {
  ApiPendingNotice,
  ApproverDeniedScreen,
  useApproverIdentity,
} from "../components/approverAccess";

const PAGE_SIZE = 10;
const ALL = "all";

const emptyResult = {
  rows: [],
  totalCount: 0,
  pendingCount: 0,
  filterOptions: { submitters: [], months: [] },
};

/**
 * Screen 7 — Team Conveyance Claims (Team HRMS).
 *
 * A Reporting Manager's queue of claims raised by their direct reportees. This
 * is a triage and navigation screen only: the decision happens on Screen 6,
 * which is why every row leads there and why there is no bulk approve control
 * — a manager cannot responsibly approve a batch without opening each claim's
 * trip detail and proofs.
 *
 * Access is gated on the Manager canonical role.
 *
 * The screen holds filter and page state, sends it to the API, and renders the
 * response. What belongs in this queue is the server's answer, not this
 * file's: the search is asked with the signed-in approver's employee id under
 * searchType Team, which returns the claims currently PENDING WITH them.
 */
export function TeamConveyanceClaimsPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isReportingManager, isFinanceManager, viewerEmpId } = useApproverIdentity();
  const currencies = useClaimCurrencies();

  const [result, setResult] = useState(emptyResult);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const [status, setStatus] = useState(ALL);
  const [month, setMonth] = useState(ALL);
  const [submitterId, setSubmitterId] = useState(ALL);
  const [category, setCategory] = useState(ALL);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);

  const filtersActive =
    status !== ALL ||
    month !== ALL ||
    submitterId !== ALL ||
    category !== ALL ||
    search.trim() !== "";

  const load = useCallback(() => {
    // The queue is whatever is pending with THIS approver, so without an
    // identity there is no queue to ask for.
    if (!viewerEmpId) {
      setResult(emptyResult);
      setLoadError(t("conveyanceClaim.errorNoEmployeeId"));
      setLoading(false);
      return;
    }

    setLoading(true);
    setLoadError("");
    // No filters travel: the endpoint takes none. The queue is read whole and
    // narrowed below, which is also what makes the dropdowns' options real.
    fetchTeamClaims({ empId: viewerEmpId })
      .then(setResult)
      .catch((error) => {
        console.error("Failed to load team conveyance claims:", error);
        setResult(emptyResult);
        setLoadError(error.message || t("conveyanceApproval.errorLoadClaims"));
      })
      .finally(() => setLoading(false));
  }, [viewerEmpId, t]);

  useEffect(() => {
    if (isReportingManager) load();
    else setLoading(false);
  }, [isReportingManager, load]);

  /** Any filter change returns to page 1 — page 4 of the old result is meaningless. */
  const applyFilter = (setter) => (value) => {
    setter(value);
    setPage(1);
  };

  const resetFilters = () => {
    setStatus(ALL);
    setMonth(ALL);
    setSubmitterId(ALL);
    setCategory(ALL);
    setSearch("");
    setPage(1);
  };

  const openClaim = (claim) =>
    navigate(claimDetailPath(claim.claimId, { from: DETAIL_ORIGIN.TEAM }));

  // Mirror of the redirect on the finance register: a Finance member who
  // lands on the manager queue is sent to the tenant-wide list instead.
  if (!isReportingManager) {
    if (isFinanceManager) return <Navigate to={ALL_CLAIMS_PATH} replace />;
    return <ApproverDeniedScreen requiredRole={CANONICAL_ROLE.REPORTING_MANAGER} />;
  }

  const { pendingCount } = result;
  // A Draft has not been submitted, so it is nobody's to review — it is kept
  // out of the queue even if the search happens to return one.
  const queue = result.rows.filter(isReportableClaim);
  // Options come from the claims actually in the queue: a month or a colleague
  // with nothing in it is not worth a line in the dropdown.
  const monthKeys = monthKeysOf(queue);
  const submitters = employeeOptionsOf(queue);

  const rows = filterClaims(queue, { status, month, category, employee: submitterId, search });
  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pagedRows = rows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const columns = [
    { key: "claimNo", label: t("conveyanceClaim.claimNo") },
    { key: "submitter", label: t("conveyanceApproval.submitter") },
    { key: "category", label: t("conveyanceClaim.claimCategory") },
    { key: "period", label: t("conveyanceClaim.periodDate") },
    { key: "amount", label: t("conveyanceClaim.totalAmount"), className: "text-right" },
    { key: "status", label: t("conveyanceClaim.status") },
    { key: "updated", label: t("conveyanceClaim.lastUpdated") },
    { key: "action", label: t("common.action"), className: "text-right" },
  ];

  /**
   * Review while the claim is still waiting on this manager, View once it has
   * moved on. `awaitingAction` is the server's flag — the queue is ordered by
   * it too, so the label and the ordering can never disagree.
   */
  const rowActionLabel = (claim) =>
    claim.awaitingAction ? t("conveyanceApproval.review") : t("formActions.view");

  return (
    <AppLayout>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2.5 flex-wrap">
            <h1 className={ui.pageTitle}>{t("conveyanceApproval.teamClaimsTitle")}</h1>
            {pendingCount > 0 ? (
              <span
                className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold"
                style={{
                  backgroundColor: "var(--status-submitted-bg)",
                  color: "var(--status-submitted-fg)",
                }}
              >
                {t("conveyanceApproval.pendingCount", { count: pendingCount })}
              </span>
            ) : null}
          </div>
        </div>
      </div>

      <ApiPendingNotice />

      {loadError ? (
        <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3">
          <p className="text-sm font-semibold text-rose-700">{loadError}</p>
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1.5 h-8 shrink-0 rounded-lg border border-rose-300 bg-white px-3 text-[11px] font-bold text-rose-700 hover:bg-rose-100 transition cursor-pointer"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            {t("localConveyance.retry")}
          </button>
        </div>
      ) : null}

      <ClaimFilterBar
        search={search}
        onSearchChange={applyFilter(setSearch)}
        searchPlaceholder={t("conveyanceApproval.searchPlaceholder")}
        filters={[
          {
            key: "status",
            label: t("conveyanceClaim.status"),
            value: status,
            onChange: applyFilter(setStatus),
            options: [
              { value: ALL, label: t("conveyanceClaim.allStatuses") },
              ...REPORT_CLAIM_STATUSES.map((value) => ({
                value,
                label: t(CLAIM_STATUS_LABEL_KEY[value]),
              })),
            ],
          },
          {
            key: "month",
            label: t("conveyanceClaim.claimMonth"),
            value: month,
            onChange: applyFilter(setMonth),
            options: [
              { value: ALL, label: t("conveyanceClaim.allMonths") },
              ...monthKeys.map((key) => ({ value: key, label: formatMonthKey(key, t) })),
            ],
          },
          {
            key: "submitter",
            label: t("conveyanceApproval.submitter"),
            value: submitterId,
            onChange: applyFilter(setSubmitterId),
            options: [
              { value: ALL, label: t("conveyanceApproval.allSubmitters") },
              ...submitters,
            ],
          },
          {
            key: "category",
            label: t("common.category"),
            value: category,
            onChange: applyFilter(setCategory),
            options: [
              { value: ALL, label: t("conveyanceClaim.allCategories") },
              ...Object.values(CLAIM_CATEGORY).map((value) => ({
                value,
                label: t(CLAIM_CATEGORY_LABEL_KEY[value]),
              })),
            ],
          },
        ]}
        allValue={ALL}
        onReset={filtersActive ? resetFilters : undefined}
      />

      <ContentCard>
        {loading ? (
          <LoadingState message={t("conveyanceApproval.loadingClaims")} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Users}
            title={
              filtersActive
                ? t("conveyanceApproval.emptyFilteredTitle")
                : t("conveyanceApproval.teamEmptyTitle")
            }
            message={
              filtersActive
                ? t("conveyanceApproval.emptyFilteredMessage")
                : t("conveyanceApproval.teamEmptyMessage")
            }
          />
        ) : (
          <>
            <DataTable columns={columns} minWidth="960px">
              {pagedRows.map((claim) => (
                <tr
                  key={claim.claimId}
                  onClick={() => openClaim(claim)}
                  className={cn(
                    "cursor-pointer transition hover:bg-workspace/20",
                    // Unactioned claims sort to the top; tinting them keeps the
                    // boundary between the two groups visible after a scroll.
                    claim.awaitingAction && "bg-accent/4"
                  )}
                >
                  <td className="px-4 py-3 font-semibold text-foreground">
                    {claim.claimNo || `#${claim.claimId}`}
                  </td>
                  <td className="px-4 py-3 text-foreground">{claimSubmitterName(claim)}</td>
                  <td className="px-4 py-3 per-file-hint">
                    {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)}
                  </td>
                  <td className="px-4 py-3 per-file-hint">
                    {formatClaimPeriod(claim, t)}
                  </td>
                  <td className="px-4 py-3 text-right font-semibold text-foreground">
                    {formatAmount(claim.totalAmount, abbreviationFor(currencies, claim.metricId))}
                  </td>
                  <td className="px-4 py-3">
                    <ClaimStatusBadge status={claim.claimStatus} />
                  </td>
                  <td className="px-4 py-3 text-[11px] per-file-hint">
                    {formatStamp(claim.modifiedOn || claim.submittedOn || claim.createdOn, t) || "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[11px] font-bold text-primary">
                      {rowActionLabel(claim)}
                    </span>
                  </td>
                </tr>
              ))}
            </DataTable>

            {/* Mobile cards */}
            <div className="md:hidden divide-y divide-(--color-text-border)/40">
              {pagedRows.map((claim) => (
                <button
                  key={claim.claimId}
                  type="button"
                  onClick={() => openClaim(claim)}
                  className={cn(
                    "w-full p-4 text-left transition hover:bg-workspace/20",
                    claim.awaitingAction && "bg-accent/4"
                  )}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-bold text-foreground">
                        {claim.claimNo || `#${claim.claimId}`}
                      </p>
                      <p className="mt-0.5 text-[12px] per-file-hint truncate">
                        {claimSubmitterName(claim)}
                      </p>
                    </div>
                    <ClaimStatusBadge status={claim.claimStatus} />
                  </div>

                  <p className="mt-2 text-lg font-extrabold text-foreground">
                    {formatAmount(claim.totalAmount, abbreviationFor(currencies, claim.metricId))}
                  </p>
                  <p className="text-[11px] per-file-hint">
                    {t(CLAIM_CATEGORY_LABEL_KEY[claim.category] || claim.category)} ·{" "}
                    {formatClaimPeriod(claim, t)}
                  </p>
                </button>
              ))}
            </div>

            <TablePagination
              page={page}
              totalPages={totalPages}
              totalItems={rows.length}
              pageSize={PAGE_SIZE}
              onPageChange={setPage}
            />
          </>
        )}
      </ContentCard>

      {/* Stated on the screen, not just in the spec: this list is for triage,
          and approval happens one claim at a time with its detail open. */}
      <p className="mt-3 text-[11px] per-file-hint">
        {t("conveyanceApproval.noBulkApprovalNote")}
      </p>
    </AppLayout>
  );
}

export default TeamConveyanceClaimsPage;
